import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import SubjectFilterBar from '../../components/feature/SubjectFilterBar';
import SubjectManagerModal from '../../components/feature/SubjectManagerModal';
import { supabase, LectureVideo } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useFavorites } from '../../hooks/useFavorites';
import VideoForm from './components/VideoForm';
import { useSubjectList } from '../../hooks/useSubjectList';
import { updatePageSEO, createBreadcrumbSchema, createCollectionPageSchema, createVideoListSchema, cleanupDynamicSchemas } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';

export default function VideosPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { subjects: subjectTags, refreshSubjects } = useSubjectList();
  const { isFavorited, toggleFavorite } = useFavorites('video');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [videos, setVideos] = useState<LectureVideo[]>([]);
  const [loadingState, setLoading] = useState(true);
  const [seriesOptions, setSeriesOptions] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'all' | 'mine'>('all');
  const [filterMode, setFilterMode] = useState<'all' | 'favorites'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<LectureVideo | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showSubjectManager, setShowSubjectManager] = useState(false);

  // ── 関数定義（useEffect より前に配置） ──

  const fetchVideos = async () => {
    setLoading(true);
    let query = supabase
      .from('lecture_videos')
      .select(`
        *,
        user_profiles (
          username,
          avatar_url
        )
      `)
      .order('created_at', { ascending: false });
    if (viewMode === 'mine' && user) {
      // 自分だけ：自分のuser_idの動画のみ
      query = query.eq('user_id', user.id);
    } else if (viewMode === 'all') {
      // 全体：すべての公開動画を表示（ログイン状態に関わらず）
      query = query.eq('is_public', true);
    }
    if (selectedSubject) query = query.eq('subject', selectedSubject);
    if (selectedSeries) query = query.eq('series', selectedSeries);
    const { data } = await query;
    setVideos(data || []);
    setLoading(false);
  };

  const fetchSeriesOptions = async () => {
    const { data } = await supabase.from('lecture_videos').select('series').not('series', 'is', null).order('series');
    if (data) {
      const uniqueSeries = Array.from(new Set(data.map((v) => v.series).filter(Boolean))) as string[];
      setSeriesOptions(uniqueSeries);
    }
  };

  // ── すべての useEffect を早期リターンより前に配置 ──

  useEffect(() => {
    updatePageSEO({
      title: '講義動画｜永山の勉強アーカイブ - YouTube学習動画・解説動画',
      description: '永山の勉強アーカイブの講義動画ページ。学習内容を動画で解説するYouTube講義動画を科目別に公開しています。',
      keywords: '講義動画,学習動画,YouTube,永山の勉強アーカイブ,学習記録',
      canonical: 'https://nagayama-study-archive.readdy.ai/videos',
      ogType: 'website',
    });
    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: '講義動画', url: 'https://nagayama-study-archive.readdy.ai/videos' },
    ]);
    createCollectionPageSchema({
      name: '講義動画一覧',
      description: '学習内容を動画で解説するYouTube講義動画の一覧ページ。',
      url: 'https://nagayama-study-archive.readdy.ai/videos',
    });
    return () => { cleanupDynamicSchemas(); };
  }, []);

  useEffect(() => {
    fetchVideos();
    fetchSeriesOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedSeries, viewMode, user]);

  useEffect(() => {
    if (videos.length > 0) {
      createVideoListSchema(
        videos.map((video) => ({
          name: video.title,
          description: video.description || video.title,
          thumbnailUrl: `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`,
          uploadDate: video.created_at,
          embedUrl: `https://www.youtube.com/embed/${video.youtube_id}`,
          contentUrl: video.youtube_url,
          duration: video.duration || undefined,
        }))
      );
    }
  }, [videos]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // ── 早期リターン（ローディング中） ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  // ── 早期リターン（未ログイン） ──
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden flex-1 flex flex-col">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex-1 flex flex-col justify-center py-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-video-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">講義動画</h1>
              <p className="text-lg text-teal-100">講義動画を管理し、効率的に学習します</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-play-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">動画管理</h3>
                <p className="text-sm text-teal-100">講義動画を整理して保存</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">視聴記録</h3>
                <p className="text-sm text-teal-100">視聴時間と進捗を自動記録</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-bookmark-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">お気に入り</h3>
                <p className="text-sm text-teal-100">重要な動画をブックマーク</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg"
              >
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-user-add-line mr-2"></i>新規登録
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ── 以下はログイン済みユーザー向けの処理 ──

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from('lecture_videos').delete().eq('id', id).eq('user_id', user!.id);
      if (error) throw error;
      setConfirmDeleteId(null);
      setToast({ message: '動画を削除しました', type: 'success' });
      fetchVideos();
    } catch {
      setToast({ message: '削除に失敗しました', type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const isMyVideo = (v: LectureVideo) => {
    if (!user) return false;
    const videoUserId = (v as LectureVideo & { user_id?: string }).user_id;
    return videoUserId === user.id;
  };

  const filteredVideos = filterMode === 'favorites'
    ? videos.filter(v => isFavorited(v.id))
    : videos;

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900">
      <Header />

      {toast && (
        <div className="fixed top-4 sm:top-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in px-4 w-full max-w-md">
          <div className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl shadow-lg text-xs sm:text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-sm sm:text-base`}></i>
            <span className="break-words">{toast.message}</span>
          </div>
        </div>
      )}

      <section className="pt-20 sm:pt-24 pb-10 sm:pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-orb-1 absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-3 absolute top-1/3 left-1/3 w-[350px] h-[350px] bg-teal-200/8 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-40 -right-40 w-[800px] h-[200px] bg-gradient-to-r from-transparent via-white/5 to-transparent rotate-[30deg] blur-2xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-teal-200 mb-4 sm:mb-6 animate-hero-fade-up">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">Top</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">講義動画</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 animate-hero-fade-up-delay">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight">講義動画</h1>
              <p className="text-teal-100 text-xs sm:text-sm md:text-base max-w-2xl leading-relaxed">学習内容を動画で解説しています。YouTubeにアップロードした講義動画をこちらで一覧できます。</p>
            </div>
            <div className="flex items-center gap-2 animate-hero-fade-up-delay2">
              <button
                onClick={() => setShowSubjectManager(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white/15 text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm min-h-[40px] sm:min-h-[44px]"
              >
                <i className="ri-folder-settings-line text-sm sm:text-base"></i>
                <span className="hidden sm:inline">科目・カテゴリ管理</span>
                <span className="sm:hidden">管理</span>
              </button>
              {user && (
                <button
                  onClick={() => { setEditingVideo(null); setShowForm(true); }}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white/15 text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm border border-white/10 shadow-sm min-h-[40px] sm:min-h-[44px]"
                >
                  <i className="ri-add-line text-base sm:text-lg"></i>
                  <span className="hidden sm:inline">動画を追加</span>
                  <span className="sm:hidden">追加</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {user && (
        <FadeInSection>
          <section className="py-3 sm:py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                  <button
                    onClick={() => setViewMode('all')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${viewMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-global-line mr-1 sm:mr-1.5"></i>全体
                  </button>
                  <button
                    onClick={() => setViewMode('mine')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${viewMode === 'mine' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-user-line mr-1 sm:mr-1.5"></i>自分の動画
                  </button>
                </div>

                <div className="h-5 sm:h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

                <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${filterMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-video-line mr-1 sm:mr-1.5"></i>すべて
                  </button>
                  <button
                    onClick={() => setFilterMode('favorites')}
                    className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] ${filterMode === 'favorites' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                  >
                    <i className="ri-heart-fill mr-1 sm:mr-1.5"></i>お気に入り
                  </button>
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>
      )}

      <FadeInSection>
        <section className="py-4 sm:py-6 md:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <SubjectFilterBar
                  subjects={subjectTags}
                  selectedSubject={selectedSubject}
                  onSelectSubject={setSelectedSubject}
                  onRefresh={refreshSubjects}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">シリーズで絞り込み</label>
                <select value={selectedSeries} onChange={(e) => setSelectedSeries(e.target.value)} className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white dark:bg-gray-700 dark:text-white min-h-[40px]">
                  <option value="">すべてのシリーズ</option>
                  {seriesOptions.map((series) => (<option key={series} value={series}>{series}</option>))}
                </select>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      <FadeInSection>
        <section className="py-8 sm:py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {loadingState ? (
              <div className="text-center py-12 sm:py-16"><div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : filteredVideos.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-4 sm:mb-6">
                  <i className={`${filterMode === 'favorites' ? 'ri-heart-line' : 'ri-video-line'} text-3xl sm:text-4xl text-gray-400 dark:text-gray-500`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  {filterMode === 'favorites'
                    ? 'お気に入りの動画がありません'
                    : viewMode === 'mine'
                    ? 'まだ自分の動画がありません'
                    : 'まだ動画がありません'}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 px-4">
                  {filterMode === 'favorites'
                    ? 'ハートアイコンをクリックして動画をお気に入りに追加しましょう。'
                    : viewMode === 'mine'
                    ? '「動画を追加」ボタンからYouTube動画を登録しましょう。'
                    : '講義動画が投稿されると、ここに表示されます。'}
                </p>
                {viewMode === 'mine' && user && filterMode !== 'favorites' && (
                  <button
                    onClick={() => { setEditingVideo(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm sm:text-base font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40 min-h-[44px]"
                  >
                    <i className="ri-add-line"></i>最初の動画を追加
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {filteredVideos.map((video) => (
                  <div key={video.id} className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shadow-sm">
                    <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="block">
                      <div className="relative w-full h-44 sm:h-48 bg-gray-900">
                        <img src={`https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`} alt={video.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white/90 rounded-full shadow-lg"><i className="ri-play-fill text-xl sm:text-2xl text-teal-600"></i></div>
                        </div>
                        {user && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite(video.id);
                            }}
                            className={`absolute top-2 left-2 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-all backdrop-blur-sm ${
                              isFavorited(video.id)
                                ? 'bg-red-500/90 text-white hover:bg-red-600/90'
                                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
                            }`}
                            title={isFavorited(video.id) ? 'お気に入りから削除' : 'お気に入りに追加'}
                          >
                            <i className={`${isFavorited(video.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-base sm:text-lg`}></i>
                          </button>
                        )}
                        {isMyVideo(video) && (
                          <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                            <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">自分</span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-0.5 ${(video as LectureVideo & { is_public?: boolean }).is_public !== false ? 'bg-teal-500/90 text-white' : 'bg-amber-500/90 text-white'}`}>
                              <i className={`${(video as LectureVideo & { is_public?: boolean }).is_public !== false ? 'ri-global-line' : 'ri-lock-line'} text-xs`}></i>
                              {(video as LectureVideo & { is_public?: boolean }).is_public !== false ? '公開' : '非公開'}
                            </span>
                          </div>
                        )}
                      </div>
                    </a>
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-medium rounded-full">{video.subject}</span>
                        {video.series && <span className="px-2 sm:px-2.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">{video.series}</span>}
                      </div>

                      {(video as any).user_profiles && (
                        <div className="flex items-center gap-2 mb-2">
                          {(video as any).user_profiles.avatar_url ? (
                            <img
                              src={(video as any).user_profiles.avatar_url}
                              alt={(video as any).user_profiles.username}
                              className="w-7 h-7 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white text-xs font-semibold">
                              {(video as any).user_profiles.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {(video as any).user_profiles.username}
                          </span>
                        </div>
                      )}

                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2 line-clamp-2 break-words">{video.title}</h3>
                      {video.description && <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 leading-relaxed break-words">{video.description}</p>}
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <span>{formatDate(video.created_at)}</span>
                        <div className="flex items-center gap-2">
                          {video.duration && <span>{video.duration}</span>}
                          {viewMode === 'mine' && isMyVideo(video) && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setEditingVideo(video); setShowForm(true); }}
                                className="p-1.5 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
                                title="編集"
                              >
                                <i className="ri-edit-line text-sm"></i>
                              </button>
                              {confirmDeleteId === video.id ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer whitespace-nowrap min-h-[32px]">いいえ</button>
                                  <button
                                    onClick={() => handleDelete(video.id)}
                                    disabled={deletingId === video.id}
                                    className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded cursor-pointer whitespace-nowrap disabled:opacity-50 min-h-[32px]"
                                  >
                                    {deletingId === video.id ? '...' : 'はい'}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteId(video.id)}
                                  className="p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
                                  title="削除"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </FadeInSection>

      {showForm && user && (
        <VideoForm
          userId={user.id}
          video={editingVideo}
          onSaved={() => {
            setShowForm(false);
            setEditingVideo(null);
            setToast({ message: editingVideo ? '動画を更新しました' : '動画を追加しました', type: 'success' });
            fetchVideos();
            fetchSeriesOptions();
            refreshSubjects();
          }}
          onClose={() => { setShowForm(false); setEditingVideo(null); }}
        />
      )}

      {showSubjectManager && (
        <SubjectManagerModal
          onClose={() => setShowSubjectManager(false)}
          onUpdated={() => {
            refreshSubjects();
          }}
        />
      )}

      <Footer />
    </div>
  );
}
